/**
 * Unified AI Chat API service
 */

import { apiClient, refreshAccessToken } from './client';
import { getCurrentLanguage } from '@/i18n';

// Abort a stream if no data arrives for this long (ms). Guards against a hung
// connection holding the reader open forever, since fetch has no built-in timeout.
const STREAM_IDLE_TIMEOUT_MS = 60000;

// Access auth store via global to avoid circular dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAuthStore = () => (window as any).__NEOKANBAN_AUTH_STORE__;

// Helper to get organization ID for query params
const getOrgId = (): string => {
  const { currentOrgId } = getAuthStore().getState();
  if (!currentOrgId) {
    throw new Error('No organization selected');
  }
  return currentOrgId;
};

// ===================================================================
// Types
// ===================================================================

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface ConversationAction {
  id: string;
  conversation_id: string;
  message_id: string | null;
  action_type: string;
  entity_type: string | null;
  entity_id: string | null;
  parameters: Record<string, unknown>;
  result: Record<string, unknown> | null;
  status: 'pending' | 'executed' | 'failed' | 'undone';
  executed_at: string | null; // ISO 8601 datetime string
  created_at: string;
}

export interface Conversation {
  id: string;
  user_id: string;
  organization_id: string;
  context_type: string;
  context_id: string | null;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationDetail extends Conversation {
  messages: ConversationMessage[];
  actions: ConversationAction[];
}

export interface SendMessageRequest {
  message: string;
  conversation_id?: string | undefined;
  context_type?: string | undefined;
  context_id?: string | undefined;
  stream?: boolean | undefined;
  user_locale?: string | undefined;
}

export interface SendMessageResponse {
  conversation_id: string;
  message_id: string;
  assistant_message_id: string;
  content: string;
  actions: ConversationAction[];
  streaming: boolean;
}

export interface ConfirmActionRequest {
  confirmed: boolean;
  modifications?: Record<string, unknown> | undefined;
}

export interface UndoActionRequest {
  reason?: string | undefined;
}

// ===================================================================
// API Methods
// ===================================================================

export const chatApi = {
  /**
   * List conversations for the current user
   */
  listConversations: async (params?: {
    context_type?: string;
    context_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<Conversation[]> => {
    const response = await apiClient.get<Conversation[]>('/chat', {
      params: {
        organization_id: getOrgId(),
        ...params,
      },
    });
    return response.data;
  },

  /**
   * Get a conversation with messages and actions
   */
  getConversation: async (conversationId: string): Promise<ConversationDetail> => {
    const response = await apiClient.get<ConversationDetail>(`/chat/${conversationId}`, {
      params: { organization_id: getOrgId() },
    });
    return response.data;
  },

  /**
   * Delete a conversation
   */
  deleteConversation: async (conversationId: string): Promise<void> => {
    await apiClient.delete(`/chat/${conversationId}`, {
      params: { organization_id: getOrgId() },
    });
  },

  /**
   * Send a message and get AI response (non-streaming)
   */
  sendMessage: async (request: SendMessageRequest): Promise<SendMessageResponse> => {
    const response = await apiClient.post<SendMessageResponse>(
      '/chat/message',
      {
        message: request.message,
        conversation_id: request.conversation_id,
        context_type: request.context_type,
        context_id: request.context_id,
        stream: false,
        user_locale: getCurrentLanguage(), // Send user's language preference
      },
      {
        params: { organization_id: getOrgId() },
      }
    );
    return response.data;
  },

  /**
   * Stream message response using fetch API
   * Returns an async generator of response content chunks
   */
  streamMessage: async function* (
    request: SendMessageRequest
  ): AsyncGenerator<string, void, unknown> {
    const config = window.__NEOKANBAN_CONFIG__ || {};
    const API_URL = config.API_URL ?? '';
    const { accessToken, currentOrgId } = getAuthStore().getState();

    if (!currentOrgId) {
      throw new Error('No organization selected');
    }

    const url = `${API_URL}/api/v1/chat/message?organization_id=${currentOrgId}`;
    const requestBody = JSON.stringify({
      message: request.message,
      conversation_id: request.conversation_id,
      context_type: request.context_type,
      context_id: request.context_id,
      stream: true,
      user_locale: getCurrentLanguage(), // Send user's language preference
    });

    // Idle-timeout guard: this request bypasses the axios client, so there is no
    // built-in timeout. Abort if no bytes arrive within STREAM_IDLE_TIMEOUT_MS.
    const controller = new AbortController();
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const resetIdleTimer = (): void => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => controller.abort(), STREAM_IDLE_TIMEOUT_MS);
    };

    const doFetch = (token: string | undefined): Promise<Response> =>
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Organization-ID': currentOrgId,
        },
        body: requestBody,
        signal: controller.signal,
      });

    resetIdleTimer();
    try {
      let response = await doFetch(accessToken);

      // The access token may have expired while the chat tab sat open. Since this
      // request bypasses the interceptor's refresh logic, refresh + retry once here
      // (sharing the interceptor's single-flight refresh lock).
      if (response.status === 401) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          response = await doFetch(newAccessToken);
        }
      }

      if (!response.ok) {
        // Backend error envelope is `{ error: { message } }` (errors.py); older/raw
        // responses may use a top-level `message`. Fall back to the HTTP status.
        const errorBody = (await response.json().catch(() => null)) as {
          error?: { message?: string };
          message?: string;
        } | null;
        throw new Error(
          errorBody?.error?.message ?? errorBody?.message ?? `HTTP ${response.status}`
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          resetIdleTimer();

          buffer += decoder.decode(value, { stream: true });

          // Process SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                return;
              }
              // Parse the JSON chunk and extract content
              try {
                const parsed = JSON.parse(data) as { content?: string };
                if (parsed.content) {
                  yield parsed.content;
                }
              } catch {
                // If not valid JSON, yield raw data (fallback)
                yield data;
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } finally {
      if (idleTimer) clearTimeout(idleTimer);
    }
  },

  /**
   * Confirm and execute a pending action
   */
  confirmAction: async (
    actionId: string,
    request: ConfirmActionRequest
  ): Promise<{ message: string; result?: Record<string, unknown> }> => {
    const response = await apiClient.post<{ message: string; result?: Record<string, unknown> }>(
      `/chat/actions/${actionId}/confirm`,
      request,
      {
        params: { organization_id: getOrgId() },
      }
    );
    return response.data;
  },

  /**
   * Undo a previously executed action
   */
  undoAction: async (
    actionId: string,
    request?: UndoActionRequest
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/chat/actions/${actionId}/undo`,
      request || {},
      {
        params: { organization_id: getOrgId() },
      }
    );
    return response.data;
  },
};
